// Mobile scroll mode — scroll progress, fade-in reveals, viewport-triggered canvases
// Usage: const { destroy, scrollToSlide } = initMobile(rootEl, { onSlideChange });

import { isMobileViewport } from './viewport.js';

export function initMobile(rootEl, options) {
  const onSlideChange = options?.onSlideChange;
  const isMobile = isMobileViewport();

  if (!isMobile) return { destroy: () => {}, scrollToSlide: () => {} };

  const progressBar = rootEl.querySelector('#scrollProgress') || rootEl.querySelector('.scroll-progress');
  const slides = rootEl.querySelectorAll('.slide');
  const observers = [];
  let currentSlideIndex = 0;

  // Hash -> scroll on load
  const hashSlide = parseInt(location.hash.replace('#', ''), 10);
  if (hashSlide >= 1 && hashSlide <= slides.length) {
    currentSlideIndex = hashSlide - 1;
    const target = slides[hashSlide - 1];
    setTimeout(() => {
      target.scrollIntoView({ behavior: 'instant', block: 'start' });
    }, 100);
  }

  // Scroll -> update hash + notify React
  let currentHash = hashSlide || 1;
  const slideObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
        const slideIdx = parseInt(entry.target.dataset.slide, 10);
        const hashIdx = slideIdx + 1;
        if (hashIdx !== currentHash) {
          currentHash = hashIdx;
          currentSlideIndex = slideIdx;
          history.replaceState(null, '', `#${hashIdx}`);
          if (onSlideChange) onSlideChange(slideIdx);
        }
      }
    });
  }, { threshold: 0.4 });
  slides.forEach(s => slideObserver.observe(s));
  observers.push(slideObserver);

  // Scroll to a specific slide (for prev/next buttons on mobile)
  function scrollToSlide(n) {
    if (n < 0 || n >= slides.length) return;
    slides[n].scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Canvas viewport activation
  const canvases = rootEl.querySelectorAll('canvas');
  const canvasObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const canvas = entry.target;
      if (entry.isIntersecting) {
        canvas.dataset.mobileActive = 'true';
        canvas.dispatchEvent(new CustomEvent('mobile-enter'));
      } else {
        canvas.dataset.mobileActive = 'false';
        canvas.dispatchEvent(new CustomEvent('mobile-leave'));
      }
    });
  }, { threshold: 0.1 });
  canvases.forEach(c => canvasObserver.observe(c));
  observers.push(canvasObserver);

  // Scroll progress bar
  let ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      if (progressBar) progressBar.style.width = progress + '%';
      ticking = false;
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  // Eval bar animation on viewport entry
  const evalFills = rootEl.querySelectorAll('.eval-fill');
  if (evalFills.length) {
    const evalObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const bar = entry.target;
          bar.style.width = '0';
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              bar.style.width = bar.dataset.width + '%';
            });
          });
          evalObserver.unobserve(bar);
        }
      });
    }, { threshold: 0.3 });
    evalFills.forEach(bar => evalObserver.observe(bar));
    observers.push(evalObserver);
  }

  // Scroll-driven animation fallback (Safari, Firefox)
  if (!CSS.supports('animation-timeline', 'view()')) {
    const allTargets = rootEl.querySelectorAll(
      '.slide .section-label, .slide h1, .slide h2, .slide .big-quote, ' +
      '.slide .body-text, .slide .subtitle, .slide .spec-quote, .slide .vision-body, ' +
      '.slide .card, .slide .agent-row, .slide .market-block, ' +
      '.slide .comparison-table, .slide .table-scroll-wrap, ' +
      '.slide .stats-row, .slide .rule-callout, ' +
      '.slide .phase-col, .slide .cta-item, .slide .flow-row, ' +
      '.slide .split-right, .slide .canvas-wrap'
    );

    allTargets.forEach(el => {
      el.style.transform = 'translateY(25px)';
      el.style.opacity = '0';
      el.style.transition = 'transform 0.6s ease-out, opacity 0.6s ease-out';
    });

    const enterObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const parent = entry.target.closest('.slide');
          const siblings = parent ? Array.from(parent.querySelectorAll('[style*="translateY"]')).filter(
            el => el.style.opacity === '0'
          ) : [];
          const idx = siblings.indexOf(entry.target);
          const delay = Math.max(0, idx) * 60;

          setTimeout(() => {
            entry.target.style.transform = 'translateY(0)';
            entry.target.style.opacity = '1';
          }, delay);

          enterObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -20px 0px' });

    allTargets.forEach(el => enterObserver.observe(el));
    observers.push(enterObserver);
  }

  // Fire initial slide change
  if (onSlideChange) onSlideChange(currentSlideIndex);

  return {
    destroy() {
      window.removeEventListener('scroll', onScroll);
      observers.forEach(obs => obs.disconnect());
    },
    scrollToSlide,
  };
}
